import { useEstabsContext } from '../../contexts/EstabsContext';
import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { useParamsContext } from '../../contexts/ParamsContext';
import { formatBRL } from '../agenda/cliente/List/helpers';
import { UserService } from '../configuracoes/usuario/Service';
import ClientSelect, { type ClientOption } from './ClientSelect';
import { useVendasContext } from './Context';
import { PERIOD_LABELS, PeriodKey, describeItems, formatDayLabel, formatTime } from './helpers';
import { ChevronRight, ShoppingBag, TriangleAlert } from 'lucide-react';

const userService = new UserService();

export default function VendasList() {
    const { estabSession } = useEstabsContext();
    const {
        filters,
        setFilters,
        vendas,
        vendasLoading,
        vendasError,
        vendasMeta,
        loadingMore,
        reloadVendas,
        loadMoreVendas,
    } = useVendasContext();
    const { errorSnackbar } = useAppContext();
    const { setParams } = useParamsContext();
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [loadingClients, setLoadingClients] = useState(true);

    useEffect(() => {
        userService
            .getClients()
            .then((response) =>
                setClients(
                    (response?.data || []).map((client: any) => ({
                        value: client.uid,
                        label: client.displayName || 'Cliente',
                    }))
                )
            )
            .catch((error) => console.error('Erro ao carregar clientes', error))
            .finally(() => setLoadingClients(false));
    }, []);

    const days = useMemo(() => {
        const groups: { label: string; rows: any[] }[] = [];

        vendas.forEach((venda) => {
            const label = formatDayLabel(venda.createdAt, estabSession?.timezone);
            const current = groups[groups.length - 1];
            if (current?.label === label) current.rows.push(venda);
            else groups.push({ label, rows: [venda] });
        });

        return groups;
    }, [vendas, estabSession?.timezone]);

    const hasFilter = filters.period !== 'all' || !!filters.clientId;
    const remaining = vendasMeta.total - vendas.length;

    const handleLoadMore = async () => {
        try {
            await loadMoreVendas();
        } catch (error) {
            console.error(error);
            errorSnackbar('Não foi possível carregar mais vendas.');
        }
    };

    const renderContent = () => {
        if (vendasLoading && !vendas.length) {
            return (
                <div className="space-y-2" aria-busy="true">
                    {[0, 1, 2, 3].map((item) => (
                        <div key={item} className="sf-skeleton h-16" />
                    ))}
                </div>
            );
        }

        if (vendasError) {
            return (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar as vendas</h2>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadVendas()}>
                        Tentar novamente
                    </button>
                </div>
            );
        }

        if (!vendas.length) {
            return (
                <div className="sf-state">
                    <ShoppingBag aria-hidden />
                    <h2>{hasFilter ? 'Nenhuma venda encontrada' : 'Nenhuma venda registrada'}</h2>
                    {hasFilter ? (
                        <button
                            type="button"
                            className="sf-link"
                            onClick={() => setFilters((prev) => ({ ...prev, period: 'all', clientId: '' }))}
                        >
                            Ver todas as vendas
                        </button>
                    ) : (
                        <p>As vendas de produtos do estoque aparecem aqui.</p>
                    )}
                </div>
            );
        }

        const ticket = vendasMeta.total ? vendasMeta.totalAmount / vendasMeta.total : 0;

        return (
            <div className={`space-y-5 transition-opacity ${vendasLoading ? 'opacity-60' : ''}`}>
                <dl className="sf-group grid grid-cols-3 divide-x divide-base-content/10 py-3 text-center">
                    <div className="min-w-0 px-2">
                        <dt className="text-xs text-base-content/55">Receita</dt>
                        <dd className="truncate text-sm font-semibold tabular-nums sm:text-lg">
                            {formatBRL(vendasMeta.totalAmount)}
                        </dd>
                    </div>
                    <div className="min-w-0 px-2">
                        <dt className="text-xs text-base-content/55">Vendas</dt>
                        <dd className="truncate text-sm font-semibold tabular-nums sm:text-lg">{vendasMeta.total}</dd>
                    </div>
                    <div className="min-w-0 px-2">
                        <dt className="text-xs text-base-content/55">Ticket médio</dt>
                        <dd className="truncate text-sm font-semibold tabular-nums sm:text-lg">{formatBRL(ticket)}</dd>
                    </div>
                </dl>

                {days.map((day) => (
                    <section key={day.label}>
                        <h2 className="sf-group-title">{day.label}</h2>
                        <ul className="sf-group">
                            {day.rows.map((venda) => (
                                <li key={venda._id}>
                                    <button
                                        type="button"
                                        className="sf-row"
                                        onClick={() => setParams({ action: 'detail', id: venda._id })}
                                    >
                                        <span className="sf-row-body">
                                            <span className="sf-row-title">{venda.clientName || 'Sem cliente'}</span>
                                            <span className="sf-row-sub">
                                                {formatTime(venda.createdAt, estabSession?.timezone)} · {describeItems(venda.items)}
                                            </span>
                                            {venda.source === 'RECORRENTE' && (
                                                <span className="sf-tags">
                                                    <span className="sf-tag" data-tone="blue">
                                                        Recorrente
                                                    </span>
                                                </span>
                                            )}
                                        </span>
                                        <span className="sf-row-end">{formatBRL(venda.total)}</span>
                                        <ChevronRight className="sf-chevron" aria-hidden />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}

                {remaining > 0 && (
                    <div className="flex justify-center">
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={loadingMore}
                            onClick={() => void handleLoadMore()}
                        >
                            {loadingMore && <span className="loading loading-spinner loading-sm"></span>}
                            Carregar mais ({remaining})
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="sf-toolbar-row">
                <select
                    className="sf-select-field"
                    aria-label="Período"
                    value={filters.period}
                    onChange={(e) => setFilters((prev) => ({ ...prev, period: e.target.value as PeriodKey }))}
                >
                    {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
                {filters.period === 'custom' && (
                    <>
                        <input
                            type="date"
                            className="sf-select-field"
                            aria-label="Data inicial"
                            value={filters.customStart}
                            max={filters.customEnd || undefined}
                            onChange={(e) => setFilters((prev) => ({ ...prev, customStart: e.target.value }))}
                        />
                        <input
                            type="date"
                            className="sf-select-field"
                            aria-label="Data final"
                            value={filters.customEnd}
                            min={filters.customStart || undefined}
                            onChange={(e) => setFilters((prev) => ({ ...prev, customEnd: e.target.value }))}
                        />
                    </>
                )}
                <div className="min-w-[14rem] flex-1 sm:max-w-sm">
                    <ClientSelect
                        value={filters.clientId}
                        onChange={(clientId) => setFilters((prev) => ({ ...prev, clientId }))}
                        clients={clients}
                        setClients={setClients}
                        loading={loadingClients}
                        canCreate={false}
                        label=""
                        placeholder="Todos os clientes"
                        menuOverlay
                    />
                </div>
            </div>

            {renderContent()}
        </>
    );
}
